import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import api, { documentApi } from '../api';
import { PageHeader, Card, Button, LoadingSpinner, StatsCard, Pagination } from '../components/UI';
import { useAuth } from '../context/AuthContext';
import * as I from 'lucide-react';

const TdsBatchesPage = ({ navigate, setSelectedPendingBatch }) => {
  const { isAuditor } = useAuth(); // AUDITOR is read-only: hide the mark-as-paid action
  // pagination state
  const [currentPage, setCurrentPage] = useState(0); // 0-based
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalBatches, setTotalBatches] = useState(0);

  const [tdsBatches, setTdsBatches] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(true);

  // payment modal state
  const [markPaidOpen, setMarkPaidOpen] = useState(false);
  const [selectedBatchForPayment, setSelectedBatchForPayment] = useState(null);

  const [batchPaidDate, setBatchPaidDate] = useState(() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    // value suitable for <input type="datetime-local">
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
  });
  const [paymentRemarks, setPaymentRemarks] = useState('');
  const [challanNumber, setChallanNumber] = useState('');
  const [challanFileName, setChallanFileName] = useState('');
  const [challanFile, setChallanFile] = useState(null);
  const [extractingChallan, setExtractingChallan] = useState(false);
  const [extractedChallanSummary, setExtractedChallanSummary] = useState(null);
  const [skipChallanExtraction, setSkipChallanExtraction] = useState(false);

  // View challan state
  const [viewChallanOpen, setViewChallanOpen] = useState(false);
  const [selectedBatchForChallan, setSelectedBatchForChallan] = useState(null);
  const [challanDetails, setChallanDetails] = useState(null);
  const [loadingChallan, setLoadingChallan] = useState(false);
  const [challanError, setChallanError] = useState('');

  // invoices-in-batch state
  const [viewInvoicesOpen, setViewInvoicesOpen] = useState(false);
  const [selectedBatchForInvoices, setSelectedBatchForInvoices] = useState(null);
  const [batchInvoices, setBatchInvoices] = useState([]);
  const [loadingBatchInvoices, setLoadingBatchInvoices] = useState(false);
  const [batchInvoicesError, setBatchInvoicesError] = useState('');
  const [invoiceSearchTerm, setInvoiceSearchTerm] = useState('');
  const [tdsInterestData, setTdsInterestData] = useState({});
  const [loadingTdsInterest, setLoadingTdsInterest] = useState({});

  // vendor cache
  const [vendorMap, setVendorMap] = useState({});

  // pdf viewer
  const [showPdf, setShowPdf] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);

  const handleViewChallanDocument = async () => {
    if (!selectedBatchForChallan?.id) {
      toast.warning('No batch selected for viewing challan.');
      return;
    }

    try {
      console.log('[TDS-BATCHES] Fetching challan document URL for batch:', selectedBatchForChallan.id);

      // Get presigned URL from backend using the new S3 endpoint
      const response = await documentApi.getTdsChallanDocumentUrl(selectedBatchForChallan.id);
      const documentUrl = response.data; // Backend returns string directly
      console.log('[TDS-BATCHES] Received presigned URL:', documentUrl);

      if (!documentUrl) {
        toast.warning('No challan document available for this batch.');
        return;
      }

      setPdfUrl(documentUrl);
      setShowPdf(true);
      console.log('[TDS-BATCHES] PDF URL set and modal shown');
    } catch (error) {
      console.error('[TDS-BATCHES] Error fetching challan document URL:', error);
      if (error.response?.status === 404) {
        toast.warning('No challan document is available for this batch.');
      } else {
        toast.error('Failed to fetch challan document. Please try again or contact support.');
      }
    }
  };

  // Fetch all TDS batches with detailed information
  const fetchTdsBatches = useCallback(async () => {
    setLoadingBatches(true);
    try {
      // First, get the list of all batches
      const batchesResp = await api.get('/tds/batches/all');
      const batches = Array.isArray(batchesResp.data) ? batchesResp.data : [];

      // Then, fetch detailed information for each batch to get the interest amount
      const batchesWithDetails = await Promise.all(batches.map(async (batch) => {
        try {
          const detailsResp = await api.get(`/tds/batches/${batch.id}`);
          return { ...batch, ...detailsResp.data };
        } catch (err) {
          console.error(`Error fetching details for batch ${batch.id}:`, err);
          return batch; // Return the original batch if details fetch fails
        }
      }));

      setTdsBatches(batchesWithDetails);
      setTotalBatches(batchesWithDetails.length);
      setTotalPages(1);
    } catch (err) {
      console.error('fetch tds batches error', err);
      setTdsBatches([]);
      setTotalBatches(0);
      setTotalPages(0);
    } finally {
      setLoadingBatches(false);
    }
  }, []);

  useEffect(() => {
    fetchTdsBatches();
  }, [fetchTdsBatches]);

  // helper formatters
  const formatCurrency = (amount) => {
    if (amount == null || Number.isNaN(Number(amount))) return '-';
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 }).format(Number(amount));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return 'Invalid Date';
    return d.toLocaleDateString('en-IN');
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return 'Invalid Date';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const datePart = `${day}/${month}/${year}`;
    const timePart = d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });
    return `${datePart} ${timePart}`;
  };

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const fetchChallanNumberForBatch = async (batchId) => {
    if (!batchId) return null;
    try {
      const resp = await api.get(`/tds/batches/${batchId}`);
      const batch = resp?.data || {};
      return batch.challanNumber || null;
    } catch (error) {
      console.error('Failed to fetch challan number for batch', batchId, error);
      return null;
    }
  };

  // Filing Quarter state
  const [filingYear, setFilingYear] = useState(2024);
  const [filingQuarterEnum, setFilingQuarterEnum] = useState('Q1');

  const openMarkAsPaidModal = (batch) => {
    setSelectedBatchForPayment(batch);
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    setBatchPaidDate(`${yyyy}-${mm}-${dd}T${hh}:${mi}`);
    setPaymentRemarks('');
    setChallanNumber('');
    setChallanFileName('');
    setChallanFile(null);
    setExtractedChallanSummary(null);
    setSkipChallanExtraction(false);

    // Default to current FY/Quarter? For now hardcoded defaults or logic
    // Logic: if month is Apr-Jun -> Q1, etc.
    // Financial Year starts Apr.
    // If today is Jan 2025, it is Q4 FY2024-25.
    const currentMonth = d.getMonth(); // 0-11
    let fy = yyyy;
    let q = 'Q1';
    if (currentMonth >= 3 && currentMonth <= 5) { q = 'Q1'; fy = yyyy; }       // Apr-Jun
    else if (currentMonth >= 6 && currentMonth <= 8) { q = 'Q2'; fy = yyyy; }  // Jul-Sep
    else if (currentMonth >= 9 && currentMonth <= 11) { q = 'Q3'; fy = yyyy; } // Oct-Dec
    else { q = 'Q4'; fy = yyyy - 1; }                                          // Jan-Mar

    setFilingYear(fy);
    setFilingQuarterEnum(q);

    setMarkPaidOpen(true);
  };

  const closeMarkAsPaidModal = () => {
    setMarkPaidOpen(false);
    setSelectedBatchForPayment(null);
  };

  const handleConfirmMarkAsPaid = async () => {
    if (!selectedBatchForPayment || !selectedBatchForPayment.id) {
      toast.error('Unable to determine batch id.');
      return;
    }

    // Construct filingQuarter string: "Qx FYyyyy-yy"
    // e.g. Q1 FY2024-25
    const nextYearShort = String(filingYear + 1).slice(-2);
    const filingQuarterStr = `${filingQuarterEnum} FY${filingYear}-${nextYearShort}`;

    try {
      await api.post(`/tds/batches/${selectedBatchForPayment.id}/mark-paid`, {
        batchPaidDate,
        paymentRemarks,
        challanNumber,
        filingQuarter: filingQuarterStr
      });

      toast.success('Batch marked as PAID and invoices updated to TDS_PAID');
      setMarkPaidOpen(false);
      setSelectedBatchForPayment(null);
      // refetch current page
      fetchTdsBatches();
    } catch (err) {
      console.error('Mark batch as paid error', err);
      const msg = err?.response?.data?.message || 'Failed to mark batch as paid.';
      toast.error(msg);
    }
  };

  const handleExtractChallanDetails = async () => {
    if (!selectedBatchForPayment || !selectedBatchForPayment.id) {
      toast.error('Unable to determine batch id.');
      return;
    }

    if (!challanFile) {
      toast.warning('Please select a challan file before extracting details.');
      return;
    }

    try {
      setExtractingChallan(true);
      const formData = new FormData();
      formData.append('file', challanFile);

      // Try to run extraction (may still succeed even if the HTTP call ends with 5xx)
      try {
        await api.post(
          `/tds/batches/${selectedBatchForPayment.id}/challan-upload`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
      } catch (uploadErr) {
        console.error('Challan upload returned an error, checking backend state anyway...', uploadErr);
        // Continue to check backend for challan number even if this call failed
      }

      // Fetch challan number with a short retry loop to allow backend commit to complete
      let latestChallanNumber = null;
      for (let attempt = 0; attempt < 3 && !latestChallanNumber; attempt += 1) {
        latestChallanNumber = await fetchChallanNumberForBatch(selectedBatchForPayment.id);
        if (!latestChallanNumber) {
          await delay(400);
        }
      }

      if (latestChallanNumber) {
        setChallanNumber(latestChallanNumber);
        toast.success('Challan details extracted successfully.');

        // Also fetch and show full challan details inline in this modal
        try {
          const challanResp = await api.get(`/tds/batches/${selectedBatchForPayment.id}/challan`);
          setExtractedChallanSummary(challanResp.data || null);
        } catch (fetchErr) {
          console.error('Failed to load challan details after extraction', fetchErr);
        }
      } else {
        toast.warning('Extraction completed but challan number was not found in the system. Please enter it manually.');
      }
    } catch (err) {
      console.error('Error extracting challan details', err);
      const msg = err?.response?.data?.message || 'Failed to extract challan details. Please try again.';
      toast.error(msg);
    } finally {
      setExtractingChallan(false);
    }
  };

  const openViewChallanModal = async (batch) => {
    if (!batch || !batch.id) return;
    setSelectedBatchForChallan(batch);
    setViewChallanOpen(true);
    setChallanDetails(null);
    setChallanError('');
    setLoadingChallan(true);

    try {
      const resp = await api.get(`/tds/batches/${batch.id}/challan`);
      setChallanDetails(resp.data || null);
    } catch (err) {
      console.error('Error loading challan details', err);
      const msg = err?.response?.data?.message || 'Failed to load challan details.';
      setChallanError(msg);
    } finally {
      setLoadingChallan(false);
    }
  };

  const closeViewChallanModal = () => {
    setViewChallanOpen(false);
    setSelectedBatchForChallan(null);
    setChallanDetails(null);
    setChallanError('');
  };

  // view invoices
  const fetchTdsInterestForInvoice = async (invoiceId) => {
    if (!invoiceId) return null;

    setLoadingTdsInterest(prev => ({ ...prev, [invoiceId]: true }));

    try {
      const response = await api.get(`/internal/invoices/${invoiceId}/tds-interest-current`);
      setTdsInterestData(prev => ({
        ...prev,
        [invoiceId]: {
          months: response.data.months || 0,
          interest: response.data.interest || 0
        }
      }));
      return response.data;
    } catch (error) {
      console.error(`Error fetching TDS interest for invoice ${invoiceId}:`, error);
      // Set default values in case of error
      setTdsInterestData(prev => ({
        ...prev,
        [invoiceId]: {
          months: 0,
          interest: 0
        }
      }));
      return { months: 0, interest: 0 };
    } finally {
      setLoadingTdsInterest(prev => ({ ...prev, [invoiceId]: false }));
    }
  };

  const openViewInvoicesModal = async (batch) => {
    setSelectedBatchForInvoices(batch);
    setViewInvoicesOpen(true);
    setBatchInvoices([]);
    setBatchInvoicesError('');
    setTdsInterestData({});
    setLoadingTdsInterest({});

    if (!batch || batch.id == null) {
      setBatchInvoicesError('Batch id is missing; unable to load invoices.');
      return;
    }

    setLoadingBatchInvoices(true);
    try {
      const resp = await api.get(`/invoices/by-tds-batch/${batch.id}`);
      const data = Array.isArray(resp.data) ? resp.data : [];
      setBatchInvoices(data);

      // Fetch TDS interest for each invoice
      if (data.length > 0) {
        await Promise.all(data.map(invoice => {
          if (invoice.id) {
            return fetchTdsInterestForInvoice(invoice.id);
          }
          return Promise.resolve();
        }));
      }

      if (!data.length) setBatchInvoicesError('No invoices found for this batch.');
    } catch (err) {
      console.error('Error loading invoices for batch', err);
      const msg = err?.response?.data?.message || 'Failed to load invoice details for this batch.';
      setBatchInvoicesError(msg);
    } finally {
      setLoadingBatchInvoices(false);
    }
  };

  const closeViewInvoicesModal = () => {
    setViewInvoicesOpen(false);
    setSelectedBatchForInvoices(null);
    setBatchInvoices([]);
    setBatchInvoicesError('');
    setInvoiceSearchTerm('');
  };

  // vendors for invoices
  const fetchVendorsForBatchInvoices = useCallback(async (invoices) => {
    try {
      const vendorIds = [...new Set(invoices.map((inv) => inv.vendorId).filter((id) => id != null && !vendorMap[id]))];
      if (!vendorIds.length) return;
      const responses = await Promise.all(
        vendorIds.map((id) => api.get(`/vendors/${id}`).then((res) => ({ id, name: res.data?.name || `Vendor ${id}` })).catch(() => ({ id, name: `Vendor ${id}` })))
      );
      const newMap = {};
      responses.forEach(({ id, name }) => (newMap[id] = name));
      if (Object.keys(newMap).length) setVendorMap((prev) => ({ ...prev, ...newMap }));
    } catch (err) {
      console.error('Error fetching vendors for batch invoices', err);
    }
  }, [vendorMap]);

  useEffect(() => {
    if (batchInvoices.length > 0) fetchVendorsForBatchInvoices(batchInvoices);
  }, [batchInvoices, fetchVendorsForBatchInvoices]);
  const filteredBatchInvoices = React.useMemo(() => {
    const term = invoiceSearchTerm.trim().toLowerCase();

    return batchInvoices.filter((inv) => {
      if (!term) return true;

      const vendorNameResolved =
        inv.vendorName ||
        (inv.vendor && inv.vendor.name) ||
        (inv.vendorId != null ? vendorMap[inv.vendorId] : '') ||
        '';

      const vendorName = vendorNameResolved.toLowerCase();
      const invoiceNumber = (inv.invoiceNumber || '').toString().toLowerCase();

      return (
        (invoiceNumber && invoiceNumber.includes(term)) ||
        (vendorName && vendorName.includes(term))
      );
    });
  }, [batchInvoices, invoiceSearchTerm, vendorMap]);

  // If backend supports pagination the filtered list is the fetched page
  const filteredBatches = tdsBatches.filter((b) => {
    const status = (b.status || '').toUpperCase();
    // This page now shows only pending / not-paid batches
    return status !== 'PAID';
  });

  const stats = {
    existingBatches: totalBatches || filteredBatches.length,
    totalTdsInBatches: (tdsBatches || []).reduce((s, b) => s + (Number(b.finalTdsAmount) || Number(b.totalTdsAmount || b.totalTds || 0) + Number(b.totalInterest || 0)), 0),
  };

  // handle page change from Pagination component (1-based in UI)
  const handlePageChange = (page) => {
    // Pagination component may send 1-based page; convert to 0-based
    const newPage = Number(page) >= 1 ? Number(page) - 1 : 0;
    setCurrentPage(newPage);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 bg-gray-50 min-h-screen">
      <PageHeader title="TDS Batches" subtitle="View pending TDS batches and mark them as paid." actions={[
        <Button key="refresh" variant="outline" onClick={fetchTdsBatches}><I.RefreshCw className={`w-4 h-4 mr-2 ${loadingBatches ? 'animate-spin' : ''}`} />Refresh</Button>
      ]} />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <StatsCard title="Existing Batches" value={`${stats.existingBatches}`} icon={I.Package} />
        <StatsCard title="Total TDS + Interest" value={formatCurrency(stats.totalTdsInBatches)} icon={I.IndianRupee} />
      </div>

      <Card>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-medium">Existing TDS Batches (Pending)</h3>
          <div className="inline-flex rounded-md shadow-sm border border-gray-200 bg-white overflow-hidden">
            <button
              type="button"
              className="px-3 py-1 text-xs font-medium bg-blue-600 text-white focus:outline-none"
              onClick={() => navigate && navigate('tds-batches')}
            >
              Pending
            </button>
            <button
              type="button"
              className="px-3 py-1 text-xs font-medium bg-white text-gray-700 hover:bg-gray-50 focus:outline-none"
              onClick={() => navigate && navigate('tds-batches-paid')}
            >
              Paid
            </button>
          </div>
        </div>

        {loadingBatches ? (
          <div className="flex items-center justify-center py-8">
            <LoadingSpinner size="md" />
            <span className="ml-3">Loading batches...</span>
          </div>
        ) : filteredBatches.length === 0 ? (
          <div className="text-sm text-gray-500">No pending TDS batches found.</div>
        ) : (
          <div className="overflow-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Batch #</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">TDS Rule</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total TDS</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total Interest</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Final TDS</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Invoices</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredBatches.map((b, idx) => (
                  <tr key={b.id || b.batchNumber || idx}>
                    <td className="px-4 py-2 text-sm text-gray-700">{b.batchNumber || b.id || `#${idx + 1}`}</td>
                    <td className="px-4 py-2 text-sm text-gray-700">{b.tdsRule || b.tdsRuleName || '-'}</td>
                    <td className="px-4 py-2 text-sm text-right text-gray-700">{formatCurrency(b.totalTdsAmount || b.totalTds || 0)}</td>
                    <td className="px-4 py-2 text-sm text-right text-gray-700">
                      {b.status === 'PAID'
                        ? formatCurrency(b.totalInterestAtPayment || 0)
                        : formatCurrency(b.totalInterest || 0)
                      }
                    </td>
                    <td className="px-4 py-2 text-sm text-right text-gray-700">
                      {b.status === 'PAID'
                        ? formatCurrency(b.finalTdsAmountAtPayment || (b.totalTdsAmount || b.totalTds || 0) + (b.totalInterestAtPayment || 0))
                        : formatCurrency(b.finalTdsAmount || (b.totalTdsAmount || b.totalTds || 0) + (b.totalInterest || 0))
                      }
                    </td>
                    <td className="px-4 py-2 text-sm">
                      <button
                        type="button"
                        onClick={() => {
                          if (setSelectedPendingBatch) {
                            setSelectedPendingBatch(b);
                          }
                          if (navigate) {
                            navigate('tds-pending-batch-invoices');
                          }
                        }}
                        className="inline-flex items-center justify-center rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100 focus:outline-none"
                      >
                        <I.FileText className="w-3 h-3 mr-1" />
                        {Array.isArray(b.invoiceIds) ? `${b.invoiceIds.length} invoice(s)` : (b.invoiceCount != null ? `${b.invoiceCount} invoice(s)` : 'View')}
                      </button>
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {!isAuditor && (
                        <button
                          type="button"
                          onClick={() => openMarkAsPaidModal(b)}
                          className="inline-flex items-center justify-center rounded-md border border-blue-200 bg-white px-3 py-1 text-xs font-medium text-blue-700 shadow-sm hover:bg-blue-50 focus:outline-none"
                        >
                          Mark as Paid
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination disabled for now */}
      </Card>

      {/* Mark as Paid Modal */}
      {markPaidOpen && selectedBatchForPayment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md text-xs sm:text-sm">
            <div className="px-3 py-2 border-b flex items-center justify-between">
              <h3 className="text-base font-semibold">Mark Batch as Paid</h3>
              <button
                className="text-gray-500 hover:text-gray-700"
                onClick={async () => {
                  if (selectedBatchForPayment && selectedBatchForPayment.id && extractedChallanSummary) {
                    try {
                      await api.delete(`/tds/batches/${selectedBatchForPayment.id}/challan`);
                    } catch (err) {
                      console.error('Failed to clear challan details on header close', err);
                      const msg = err?.response?.data?.message || 'Failed to clear challan details. They may still be saved.';
                      toast.error(msg);
                    }
                  }
                  closeMarkAsPaidModal();
                }}
              >
                ×
              </button>
            </div>

            <div className="px-3 py-3 space-y-3">
              <div className="grid grid-cols-2 gap-3 text-xs sm:text-sm">
                <div>
                  <div className="text-gray-500">Batch #</div>
                  <div className="font-medium">{selectedBatchForPayment.batchNumber || selectedBatchForPayment.id}</div>
                </div>
                <div>
                  <div className="text-gray-500">TDS Rule</div>
                  <div className="font-medium">{selectedBatchForPayment.tdsRule || selectedBatchForPayment.tdsRuleName || '-'}</div>
                </div>
                <div>
                  <div className="text-gray-500">Total TDS Amount</div>
                  <div className="font-medium">{formatCurrency(selectedBatchForPayment.totalTdsAmount || selectedBatchForPayment.totalTds || 0)}</div>
                </div>
                <div>
                  <div className="text-gray-500">Total Interest</div>
                  <div className="font-medium">{formatCurrency(selectedBatchForPayment.totalInterest || 0)}</div>
                </div>
                <div>
                  <div className="text-gray-500">Final TDS Amount</div>
                  <div className="font-medium text-lg">{formatCurrency(selectedBatchForPayment.finalTdsAmount || (selectedBatchForPayment.totalTdsAmount || selectedBatchForPayment.totalTds || 0) + (selectedBatchForPayment.totalInterest || 0))}</div>
                </div>
                <div>
                  <label className="text-gray-500 block mb-1" htmlFor="tds-payment-date">Payment Marked Date &amp; Time</label>
                  <input
                    id="tds-payment-date"
                    type="datetime-local"
                    value={batchPaidDate}
                    readOnly
                    disabled
                    className="w-full border border-gray-200 bg-gray-100 text-gray-600 rounded px-2 py-1 text-sm cursor-not-allowed"
                  />
                </div>

                {/* Filing Quarter Inputs */}
                <div>
                  <label className="text-gray-500 block mb-1">Filing Financial Year</label>
                  <select
                    value={filingYear}
                    onChange={(e) => setFilingYear(Number(e.target.value))}
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                  >
                    {(() => {
                      const currentYear = new Date().getFullYear();
                      // Generate range from 2023 up to currentYear + 1 (to handle near future)
                      // adjust range size as needed
                      const startYear = 2023;
                      const endYear = currentYear + 1;
                      const years = [];
                      for (let y = startYear; y <= endYear; y++) {
                        years.push(y);
                      }
                      return years.map(y => (
                        <option key={y} value={y}>FY {y}-{String(y + 1).slice(-2)}</option>
                      ));
                    })()}
                  </select>
                </div>
                <div>
                  <label className="text-gray-500 block mb-1">Filing Quarter</label>
                  <select
                    value={filingQuarterEnum}
                    onChange={(e) => setFilingQuarterEnum(e.target.value)}
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                  >
                    <option value="Q1">Q1 (Apr-Jun)</option>
                    <option value="Q2">Q2 (Jul-Sep)</option>
                    <option value="Q3">Q3 (Oct-Dec)</option>
                    <option value="Q4">Q4 (Jan-Mar)</option>
                  </select>
                </div>
              </div>

              <div className="text-xs sm:text-sm">
                <label className="text-gray-500 block mb-1" htmlFor="tds-payment-remarks">
                  Remarks
                  {!extractedChallanSummary && !skipChallanExtraction && <span className="text-red-500 ml-1">*</span>}
                </label>
                <input
                  id="tds-payment-remarks"
                  type="text"
                  value={paymentRemarks}
                  onChange={(e) => setPaymentRemarks(e.target.value)}
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                  placeholder={(extractedChallanSummary || skipChallanExtraction) ? "Enter any remarks about this payment" : "Remarks are required if proceeding without challan extraction"}
                />
              </div>

              <div className="flex items-center text-xs sm:text-sm">
                <input
                  id="skip-challan-extraction"
                  type="checkbox"
                  checked={skipChallanExtraction}
                  onChange={(e) => {
                    setSkipChallanExtraction(e.target.checked);
                    // Reset challan details if skipping is checked? 
                    // No, user might want to switch back. But let's clear mandatory checks.
                  }}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-2"
                />
                <label htmlFor="skip-challan-extraction" className="text-gray-700">Skip Challan Extraction (No Tds for vendor)</label>
              </div>

              {!skipChallanExtraction && (
                <div className="flex flex-col items-start text-xs sm:text-sm">
                  <label className="text-gray-500 block mb-1">Upload Challan</label>
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      setChallanFile(file || null);
                      setChallanFileName(file ? file.name : '');
                    }}
                    className="block w-full text-sm text-gray-700"
                  />
                </div>
              )}

              {extractedChallanSummary && (
                <div className="mt-3 p-3 border border-gray-200 rounded bg-gray-50 text-xs text-gray-800">
                  <div className="font-semibold mb-2">Extracted challan details</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <div className="text-gray-500">BSR Code</div>
                      <div className="font-medium">{extractedChallanSummary.bsrCode || 'N/A'}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Date of Deposit</div>
                      <div className="font-medium">{extractedChallanSummary.dateOfDeposit ? formatDate(extractedChallanSummary.dateOfDeposit) : 'N/A'}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Challan No.</div>
                      <div className="font-medium">{extractedChallanSummary.challanSerialNumber || 'N/A'}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Total Tax Amount</div>
                      <div className="font-medium">{formatCurrency(extractedChallanSummary.totalTaxAmount)}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="px-3 py-3 border-t flex justify-end gap-2 text-xs sm:text-sm">
              <Button
                variant="secondary"
                onClick={async () => {
                  if (selectedBatchForPayment && selectedBatchForPayment.id && extractedChallanSummary) {
                    try {
                      await api.delete(`/tds/batches/${selectedBatchForPayment.id}/challan`);
                    } catch (err) {
                      console.error('Failed to clear challan details on cancel', err);
                      const msg = err?.response?.data?.message || 'Failed to clear challan details. They may still be saved.';
                      toast.error(msg);
                    }
                  }
                  closeMarkAsPaidModal();
                }}
                disabled={extractingChallan}
              >
                <I.X className="w-4 h-4 mr-2" />Cancel
              </Button>
              {!skipChallanExtraction && (
                <Button
                  variant="outline"
                  onClick={handleExtractChallanDetails}
                  disabled={extractingChallan || !challanFile}
                >
                  {extractingChallan ? (
                    <>
                      <I.Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Extracting...
                    </>
                  ) : (
                    <>
                      <I.Scan className="w-4 h-4 mr-2" />
                      Extract Details
                    </>
                  )}
                </Button>
              )}
              <Button
                variant="primary"
                onClick={handleConfirmMarkAsPaid}
                disabled={extractingChallan || ((!extractedChallanSummary && !skipChallanExtraction) || ((skipChallanExtraction || !extractedChallanSummary) && !paymentRemarks.trim()))}
              >
                <I.Check className="w-4 h-4 mr-2" />Confirm
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* View Challan Modal */}
      {viewChallanOpen && selectedBatchForChallan && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-lg">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">Challan Details</h3>
              <div className="flex items-center space-x-2">
                <button onClick={handleViewChallanDocument} className="text-blue-600 hover:text-blue-800 p-1" title="View Document"><I.Eye className="w-5 h-5" /></button>
                <button className="text-gray-500 hover:text-gray-700 p-1" onClick={closeViewChallanModal}><I.X className="w-5 h-5" /></button>
              </div>
            </div>

            <div className="p-4 text-sm max-h-[60vh] overflow-auto">
              <div className="mb-3 text-gray-600"><div>Batch #: <span className="font-medium">{selectedBatchForChallan.batchNumber || selectedBatchForChallan.id}</span></div></div>

              {loadingChallan ? (
                <div className="flex items-center justify-center py-6"><LoadingSpinner size="sm" /><span className="ml-2 text-gray-600">Loading challan details...</span></div>
              ) : challanError ? (
                <div className="text-red-600">{challanError}</div>
              ) : !challanDetails ? (
                <div className="text-gray-500">No challan details found for this batch.</div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><div className="text-gray-500">BSR Code</div><div className="font-medium">{challanDetails.bsrCode || 'N/A'}</div></div>
                  <div><div className="text-gray-500">Date of Deposit</div><div className="font-medium">{challanDetails.dateOfDeposit ? formatDate(challanDetails.dateOfDeposit) : 'N/A'}</div></div>
                  <div><div className="text-gray-500">Challan No.</div><div className="font-medium">{challanDetails.challanSerialNumber || 'N/A'}</div></div>
                  <div><div className="text-gray-500">TAN</div><div className="font-medium">{challanDetails.tan || 'N/A'}</div></div>
                  <div><div className="text-gray-500">Assessment Year</div><div className="font-medium">{challanDetails.assessmentYear || 'N/A'}</div></div>
                  <div><div className="text-gray-500">Major Head</div><div className="font-medium">{challanDetails.majorHead || 'N/A'}</div></div>
                  <div><div className="text-gray-500">Minor Head</div><div className="font-medium">{challanDetails.minorHead || 'N/A'}</div></div>
                  <div><div className="text-gray-500">Nature of Payment</div><div className="font-medium">{challanDetails.natureOfPayment || 'N/A'}</div></div>
                  <div><div className="text-gray-500">Total Tax Amount</div><div className="font-medium">{formatCurrency(challanDetails.totalTaxAmount)}</div></div>
                  <div><div className="text-gray-500">CIN</div><div className="font-medium">{challanDetails.cin || 'N/A'}</div></div>
                </div>
              )}
            </div>

            <div className="p-4 border-t flex justify-end gap-3"><Button variant="secondary" onClick={closeViewChallanModal}>Close</Button></div>
          </div>
        </div>
      )}

      {/* View Invoices Modal */}
      {viewInvoicesOpen && selectedBatchForInvoices && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-5xl text-xs sm:text-sm">
            <div className="p-4 border-b flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">Invoices in Batch</h3>
                <p className="text-sm text-gray-500">
                  Batch: <span className="font-medium">{selectedBatchForInvoices.batchNumber || selectedBatchForInvoices.id}</span>
                </p>
              </div>
              <button className="text-gray-500 hover:text-gray-700" onClick={closeViewInvoicesModal}>×</button>
            </div>

            <div className="p-4 max-h-[70vh] overflow-auto text-xs">
              <div className="flex items-center justify-between gap-3 mb-3">
                <div className="text-sm font-medium text-gray-700">Invoice Details</div>
                <div className="flex items-center gap-2 text-xs">
                  <input
                    type="text"
                    value={invoiceSearchTerm}
                    onChange={(e) => setInvoiceSearchTerm(e.target.value)}
                    placeholder="Search by invoice # or vendor..."
                    className="border border-gray-300 rounded px-2 py-1 text-xs w-48"
                  />
                </div>
              </div>

              {loadingBatchInvoices ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner size="md" />
                  <span className="ml-3 text-gray-600">Loading invoices...</span>
                </div>
              ) : batchInvoicesError ? (
                <div className="text-red-600">{batchInvoicesError}</div>
              ) : filteredBatchInvoices.length === 0 ? (
                <div className="text-gray-500">No invoices found for this batch.</div>
              ) : (
                <div className="overflow-auto text-xs">
                  <table className="min-w-full divide-y divide-gray-200 text-xs">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          <div className="sticky left-0 bg-gray-50 z-10 pl-2 pr-4 -ml-2">Invoice #</div>
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Invoice Date</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Vendor Name</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">TDS Amount</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">TDS Default Months</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">TDS Interest</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Invoice Payable</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredBatchInvoices.map((inv) => {
                        const vendorName =
                          inv.vendorName ||
                          (inv.vendor && inv.vendor.name) ||
                          (inv.vendorId != null ? vendorMap[inv.vendorId] : `Vendor ${inv.vendorId || ''}`);
                        const tdsAmt = inv.tdsAmount != null ? inv.tdsAmount : 0;
                        const invoicePayable = inv.invoicePayable != null ? inv.invoicePayable : 0;

                        return (
                          <tr key={inv.id || inv.invoiceNumber} className="text-xs">
                            <td className="px-4 py-1 text-gray-700">
                              <div className="sticky left-0 bg-white z-10 pl-2 pr-4 -ml-2">
                                {inv.invoiceNumber || inv.id || 'N/A'}
                              </div>
                            </td>
                            <td className="px-4 py-1 text-gray-700">
                              {inv.invoiceDate ? formatDate(inv.invoiceDate) : 'N/A'}
                            </td>
                            <td className="px-4 py-1 text-gray-700">
                              <div className="min-w-[150px] max-w-[250px] break-words whitespace-normal">{vendorName}</div>
                            </td>
                            <td className="px-4 py-1 text-right text-gray-700">
                              {inv.totalAmount != null ? formatCurrency(inv.totalAmount) : 'N/A'}
                            </td>
                            <td className="px-4 py-1 text-right text-gray-700">
                              {tdsAmt != null ? formatCurrency(tdsAmt) : 'N/A'}
                            </td>
                            <td className="px-4 py-1 text-right text-gray-700">
                              {loadingTdsInterest[inv.id] ? (
                                <I.Loader2 className="w-3 h-3 animate-spin inline-block" />
                              ) : (
                                tdsInterestData[inv.id]?.months || 0
                              )}
                            </td>
                            <td className="px-4 py-1 text-right text-gray-700">
                              {loadingTdsInterest[inv.id] ? (
                                <I.Loader2 className="w-3 h-3 animate-spin inline-block" />
                              ) : (
                                tdsInterestData[inv.id]?.interest ? formatCurrency(tdsInterestData[inv.id].interest) : formatCurrency(0)
                              )}
                            </td>
                            <td className="px-4 py-1 text-right text-gray-700">
                              {formatCurrency(invoicePayable)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="p-4 border-t flex justify-end gap-3">
              <Button variant="secondary" onClick={closeViewInvoicesModal}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {showPdf && pdfUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
          <div className="bg-white rounded-lg shadow-xl w-[80vw] h-[85vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2 border-b">
              <span className="text-sm font-medium text-gray-700">Challan Document</span>
              <button type="button" onClick={() => setShowPdf(false)} className="text-gray-500 hover:text-gray-700">
                <I.X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1">
              {pdfUrl.toLowerCase().match(/\.(jpg|jpeg|png|gif|bmp|webp)$/i) ? (
                // Display image in iframe
                <iframe
                  src={pdfUrl}
                  title="Challan Document"
                  className="w-full h-full border-0"
                  frameBorder="0"
                  onLoad={() => console.log('[TDS-BATCHES] Challan image iframe loaded')}
                  onError={(e) => console.error('[TDS-BATCHES] Challan image iframe error:', e)}
                />
              ) : (
                // Display PDF with viewer parameters
                <iframe
                  src={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                  title="Challan Document"
                  className="w-full h-full border-0"
                  frameBorder="0"
                  onLoad={() => console.log('[TDS-BATCHES] Challan PDF iframe loaded')}
                  onError={(e) => console.error('[TDS-BATCHES] Challan PDF iframe error:', e)}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TdsBatchesPage;
